import { getLocalizedPackUnit } from '@/utils/item';
import { PackUnit } from '@/enums';
import {
  pickLabel,
  PricingGroupBy,
  PricingLabel,
  PricingLabelFields,
  toGroupIdentity,
} from '@/experiences/sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import { RecommendationAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { useMemo } from 'react';
import type { GetPricingRecommendationsQuery } from '@/types';
import type { PricingInvoiceHistoryEntry } from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import type {
  PricingRecommendationRow,
  PublishedProgress,
  RecommendationGroup,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/types';

// The endpoint returns published recommendations alongside active ones so a published
// view can render them. This screen is the active view, so it drops them.
const isPublished = (raw: RawRecommendation): boolean =>
  Boolean(raw.published_at);

const toReviewStatus = (status: string): RecommendationAction | null => {
  switch (status) {
    case 'ACCEPTED':
      return RecommendationAction.Accepted;
    case 'OVERRIDDEN':
      return RecommendationAction.Overridden;
    case 'REJECTED':
      return RecommendationAction.Rejected;
    default:
      return null;
  }
};

type RecommendationsResponse =
  GetPricingRecommendationsQuery['getPricingRecommendationsForBusinessOrganization'];

type RawRecommendation = RecommendationsResponse['recommendations'][number];

export interface TransformedRecommendations {
  autoPublishAt: string | null;
  groups: RecommendationGroup[];
  hasRecommendations: boolean;
  isAutoPublish: boolean;
  isPublishingEnabled: boolean;
  progress: PublishedProgress | undefined;
  rows: PricingRecommendationRow[];
}

function toProgress(
  raw: RecommendationsResponse['progress'],
): PublishedProgress {
  return {
    accepted: raw.accepted,
    overridden: raw.overridden,
    pending: raw.pending,
    rejected: raw.rejected,
  };
}

function toRow(raw: RawRecommendation): PricingRecommendationRow {
  const invoiceHistory: PricingInvoiceHistoryEntry[] =
    raw.invoice_history?.map((entry) => ({
      grossMarginPercentage: entry.gross_margin_percentage ?? null,
      invoiceDate: entry.invoice_date,
      priceInMicros: entry.price_in_micros ?? null,
      quantity: entry.quantity,
    })) ?? [];

  return {
    grossProfitInPercentage: raw.gross_profit_in_percentage ?? 0,
    invoiceHistory,
    newPriceInMicros: raw.new_price_in_micros,
    peerGroup: raw.peer_group
      ? {
          id: raw.peer_group.id,
          maxGrossProfitInPercentage:
            raw.peer_group.max_gross_profit_in_percentage,
          minGrossProfitInPercentage:
            raw.peer_group.min_gross_profit_in_percentage,
        }
      : null,
    reasoning: raw.reasoning,
    restaurantAccountId: raw.restaurant_account_id ?? '',
    restaurantName: raw.restaurant?.name ?? raw.restaurant_id,
    restaurantUUID: raw.restaurant?.uuid ?? raw.restaurant_id,
    reviewStatus: toReviewStatus(raw.review_status),
    snapshotCostInMicros: raw.snapshot_cost_in_micros,
    snapshotGrossProfitInPercentage:
      raw.snapshot_gross_profit_in_percentage ?? 0,
    snapshotPriceInMicros: raw.snapshot_price_in_micros,
    supplierUUID: raw.supplier_id,
    uuid: raw.uuid,
    variantPackExternalId: raw.variant_pack?.external_item_id ?? '',
    variantPackName: raw.variant_pack?.item.display_name ?? '',
    variantPackUOM: raw.variant_pack?.pack.unit
      ? getLocalizedPackUnit(raw.variant_pack.pack.unit as PackUnit)
      : '--',
    variantPackUUID: raw.variant_pack?.uuid ?? '',
    weeklyGPImpactInMicros: raw.weekly_gp_impact_in_micros ?? null,
    weeklyVolume: raw.weekly_volume ?? null,
  };
}

const RECOMMENDATION_LABEL_FIELDS: Record<
  PricingGroupBy,
  PricingLabelFields<PricingRecommendationRow>
> = {
  [PricingGroupBy.Restaurant]: {
    id: 'restaurantAccountId',
    key: 'restaurantUUID',
    name: 'restaurantName',
  },
  [PricingGroupBy.VariantPack]: {
    id: 'variantPackExternalId',
    key: 'variantPackUUID',
    name: 'variantPackName',
  },
};

export const recommendationLabelOf = (
  row: PricingRecommendationRow,
  dimension: PricingGroupBy,
): PricingLabel => pickLabel(row, RECOMMENDATION_LABEL_FIELDS[dimension]);

export const toGroups = (
  rows: readonly PricingRecommendationRow[],
  groupBy: PricingGroupBy = PricingGroupBy.Restaurant,
): RecommendationGroup[] => {
  const groupMap = new Map<string, RecommendationGroup>();

  for (const row of rows) {
    const label = recommendationLabelOf(row, groupBy);
    // A row missing its variant pack has no key to share, so it stays its own
    // parent instead of pooling with every other such row.
    const key = label.key || row.uuid;
    const existing = groupMap.get(key);
    if (existing) {
      existing.subRows.push(row);
      existing.itemCount += 1;
      existing.aggregateWeeklyVolume += row.weeklyVolume ?? 0;
      existing.aggregateWeeklyGPImpactInMicros +=
        row.weeklyGPImpactInMicros ?? 0;
    } else {
      groupMap.set(key, {
        ...toGroupIdentity(groupBy, { ...label, key }),
        aggregateWeeklyGPImpactInMicros: row.weeklyGPImpactInMicros ?? 0,
        aggregateWeeklyVolume: row.weeklyVolume ?? 0,
        itemCount: 1,
        subRows: [row],
      });
    }
  }

  return [...groupMap.values()];
};

export function useRecommendationTransformer(
  data: GetPricingRecommendationsQuery | null | undefined,
  groupBy: PricingGroupBy = PricingGroupBy.Restaurant,
): TransformedRecommendations {
  return useMemo(() => {
    const response = data?.getPricingRecommendationsForBusinessOrganization;
    const autoPublishAt = response?.auto_publish_at ?? null;
    const rows =
      response?.recommendations
        ?.filter((raw) => !isPublished(raw))
        .map(toRow) ?? [];
    const groups = toGroups(rows, groupBy);

    return {
      autoPublishAt,
      groups,
      hasRecommendations: groups.length > 0,
      isAutoPublish: autoPublishAt !== null,
      isPublishingEnabled: response?.publish_enabled ?? false,
      progress: response?.progress ? toProgress(response.progress) : undefined,
      rows,
    };
  }, [data, groupBy]);
}
